import { useState, useEffect } from 'react';
import { client } from '../../api/client';
import { useToast } from '../common/Toast';

interface TokenTransaction {
  id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  amount: number;
  transaction_type: string;
  transaction_type_label: string;
  description: string;
  reference_id: string | null;
  created_at: string;
}

interface PromoCode {
  id: number;
  code: string;
  tokens_amount: number;
  max_uses: number | null;
  current_uses: number;
  valid_until: string | null;
  is_active: boolean;
  description: string;
  created_at: string;
}

export function TokensTab() {
  const toast = useToast();
  const [tokenTransactions, setTokenTransactions] = useState<TokenTransaction[]>([]);
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [activeSubTab, setActiveSubTab] = useState<'transactions' | 'promos'>('transactions');
  const [isCreatingPromo, setIsCreatingPromo] = useState(false);
  const [isEditingPromo, setIsEditingPromo] = useState<PromoCode | null>(null);
  const [newPromo, setNewPromo] = useState({ code: '', tokensAmount: 5, maxUses: '', validUntil: '', description: '' });
  
  // Filter states
  const [transactionTypeFilter, setTransactionTypeFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Load token transactions
      const transactionsRes = await client.api.fetch('/admin/token-transactions');
      const transactionsData = await transactionsRes.json();
      if (transactionsData.success) {
        setTokenTransactions(transactionsData.data || []);
      }

      // Load promo codes
      const promosRes = await client.api.fetch('/admin/promo-codes');
      const promosData = await promosRes.json();
      if (promosData.success) {
        setPromoCodes(promosData.data || []);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Greška pri učitavanju podataka');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePromo = async () => {
    try {
      const res = await client.api.fetch('/admin/promo-codes', {
        method: 'POST',
        body: JSON.stringify({
          code: newPromo.code,
          tokens_amount: newPromo.tokensAmount,
          max_uses: newPromo.maxUses || null,
          valid_until: newPromo.validUntil || null,
          description: newPromo.description
        })
      });
      const data = await res.json();
      if (data.success) {
        loadData(); // Reload to get fresh data
        setIsCreatingPromo(false);
        setNewPromo({ code: '', tokensAmount: 5, maxUses: '', validUntil: '', description: '' });
        toast.success('Promo kod kreiran', 'Promo kod je uspešno kreiran!');
      } else {
        toast.error('Greška', data.error || 'Greška pri kreiranju promo koda');
      }
    } catch (err) {
      toast.error('Greška', 'Greška pri povezivanju sa serverom');
    }
  };

  const handleUpdatePromo = async () => {
    if (!isEditingPromo) return;

    try {
      const res = await client.api.fetch(`/admin/promo-codes/${isEditingPromo.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          tokens_amount: newPromo.tokensAmount,
          max_uses: newPromo.maxUses || null,
          valid_until: newPromo.validUntil || null,
          description: newPromo.description
        })
      });
      const data = await res.json();
      if (data.success) {
        loadData();
        setIsEditingPromo(null);
        setNewPromo({ code: '', tokensAmount: 5, maxUses: '', validUntil: '', description: '' });
        toast.success('Uspešno', 'Promo kod je ažuriran.');
      } else {
        toast.error('Greška', data.error || 'Greška pri ažuriranju promo koda');
      }
    } catch (err) {
      toast.error('Greška', 'Greška pri povezivanju sa serverom');
    }
  };

  const handleTogglePromo = async (id: number) => {
    try {
      const res = await client.api.fetch(`/admin/promo-codes/${id}/toggle`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setPromoCodes(promoCodes.map(p => p.id === id ? { ...p, is_active: data.is_active } : p));
      }
    } catch (err) {
      toast.error('Greška', 'Greška pri povezivanju sa serverom');
    }
  };

  const handleDeletePromo = async (id: number) => {
    toast.showConfirm({
      title: 'Brisanje promo koda',
      message: 'Da li ste sigurni da želite da obrišete ovaj promo kod?',
      confirmText: 'Obriši',
      cancelText: 'Otkaži',
      type: 'danger',
      onConfirm: async () => {
        try {
          const res = await client.api.fetch(`/admin/promo-codes/${id}`, { method: 'DELETE' });
          const data = await res.json();
          if (data.success) {
            setPromoCodes(promoCodes.filter(p => p.id !== id));
            toast.success('Uspešno', 'Promo kod je obrisan.');
          } else {
            toast.error('Greška', data.error || 'Greška pri brisanju promo koda');
          }
        } catch (err) {
          console.error('Error deleting promo code:', err);
          toast.error('Greška', 'Greška pri povezivanju sa serverom');
        }
      }
    });
  };

  const openEditPromo = (promo: PromoCode) => {
    setIsEditingPromo(promo);
    setNewPromo({
      code: promo.code,
      tokensAmount: promo.tokens_amount,
      maxUses: promo.max_uses?.toString() || '',
      validUntil: promo.valid_until?.substring(0, 10) || '',
      description: promo.description || ''
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 p-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Sub-tabs */}
      <div className="flex gap-4 border-b border-slate-200 dark:border-slate-700">
        <button
          onClick={() => setActiveSubTab('transactions')}
          className={`pb-3 px-2 font-medium transition-colors relative ${
            activeSubTab === 'transactions'
              ? 'text-amber-600'
              : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
          }`}
        >
          Transakcije tokena
          {activeSubTab === 'transactions' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber-600" />
          )}
        </button>
        <button
          onClick={() => setActiveSubTab('promos')}
          className={`pb-3 px-2 font-medium transition-colors relative ${
            activeSubTab === 'promos'
              ? 'text-amber-600'
              : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
          }`}
        >
          Promo kodovi
          {activeSubTab === 'promos' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber-600" />
          )}
        </button>
      </div>

      {activeSubTab === 'transactions' ? (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <input
                type="text"
                placeholder="Pretraži po korisniku ili opisu..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
              />
            </div>
            <select
              value={transactionTypeFilter}
              onChange={(e) => setTransactionTypeFilter(e.target.value)}
              className="px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
            >
              <option value="all">Svi tipovi</option>
              <option value="purchase">Kupovina</option>
              <option value="promo_redeem">Promo kod</option>
              <option value="listing">Objava oglasa</option>
              <option value="reactivation">Reaktivacija</option>
              <option value="admin_adjustment">Admin podešavanje</option>
              <option value="refund">Povraćaj</option>
            </select>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm overflow-hidden">
            <table className="w-full text-left">
              <thead className="bg-slate-50 dark:bg-slate-700/50">
                <tr>
                  <th className="p-4 font-semibold text-slate-900 dark:text-white">Korisnik</th>
                  <th className="p-4 font-semibold text-slate-900 dark:text-white">Tip</th>
                  <th className="p-4 font-semibold text-slate-900 dark:text-white">Tokeni</th>
                  <th className="p-4 font-semibold text-slate-900 dark:text-white">Opis</th>
                  <th className="p-4 font-semibold text-slate-900 dark:text-white">Datum</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {tokenTransactions
                  .filter(tx => {
                    if (transactionTypeFilter !== 'all' && tx.transaction_type !== transactionTypeFilter) return false;
                    if (searchTerm) {
                      const search = searchTerm.toLowerCase();
                      return tx.user_name.toLowerCase().includes(search) ||
                             tx.user_email.toLowerCase().includes(search) ||
                             (tx.description && tx.description.toLowerCase().includes(search));
                    }
                    return true;
                  })
                  .length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-8 text-center text-slate-500 dark:text-slate-400">
                      Nema transakcija tokena.
                    </td>
                  </tr>
                ) : (
                  tokenTransactions
                    .filter(tx => {
                      if (transactionTypeFilter !== 'all' && tx.transaction_type !== transactionTypeFilter) return false;
                      if (searchTerm) {
                        const search = searchTerm.toLowerCase();
                        return tx.user_name.toLowerCase().includes(search) ||
                               tx.user_email.toLowerCase().includes(search) ||
                               (tx.description && tx.description.toLowerCase().includes(search));
                      }
                      return true;
                    })
                    .map((tx) => (
                      <tr key={tx.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                        <td className="p-4">
                          <div className="font-medium text-slate-900 dark:text-white">{tx.user_name}</div>
                          <div className="text-sm text-slate-500">{tx.user_email}</div>
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            tx.transaction_type === 'purchase' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                            tx.transaction_type === 'promo_redeem' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' :
                            tx.transaction_type === 'listing' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                            tx.transaction_type === 'reactivation' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                            tx.transaction_type === 'refund' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                            'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-400'
                          }`}>
                            {tx.transaction_type_label}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className={`font-bold ${tx.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {tx.amount > 0 ? '+' : ''}{tx.amount}
                          </span>
                        </td>
                        <td className="p-4 text-slate-600 dark:text-slate-400 max-w-xs truncate">
                          {tx.description || '-'}
                        </td>
                        <td className="p-4 text-slate-600 dark:text-slate-400">
                          {new Date(tx.created_at).toLocaleString('sr-RS', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </td>
                      </tr>
                    ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex justify-end">
            <button
              onClick={() => setIsCreatingPromo(true)}
              className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Novi Promo Kod
            </button>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm overflow-hidden">
            <table className="w-full text-left">
              <thead className="bg-slate-50 dark:bg-slate-700/50">
                <tr>
                  <th className="p-4 font-semibold text-slate-900 dark:text-white">Kod</th>
                  <th className="p-4 font-semibold text-slate-900 dark:text-white">Vrednost</th>
                  <th className="p-4 font-semibold text-slate-900 dark:text-white">Dostupno</th>
                  <th className="p-4 font-semibold text-slate-900 dark:text-white">Važi do</th>
                  <th className="p-4 font-semibold text-slate-900 dark:text-white">Status</th>
                  <th className="p-4 font-semibold text-slate-900 dark:text-white">Akcije</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {promoCodes.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-slate-500 dark:text-slate-400">
                      Nema promo kodova. Kreirajte prvi promo kod.
                    </td>
                  </tr>
                ) : (
                  promoCodes.map((promo) => (
                    <tr key={promo.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                      <td className="p-4 font-mono font-bold text-slate-900 dark:text-white">{promo.code}</td>
                      <td className="p-4 font-medium text-amber-600">{promo.tokens_amount} tokena</td>
                      <td className="p-4 text-slate-600 dark:text-slate-400">
                        {promo.max_uses === null ? '∞' : (promo.max_uses - promo.current_uses)} {promo.max_uses !== null && 'vaučera'}
                      </td>
                      <td className="p-4 text-slate-600 dark:text-slate-400">
                        {promo.valid_until ? new Date(promo.valid_until).toLocaleDateString('sr-RS') : 'Trajno'}
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          promo.is_active ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-400'
                        }`}>
                          {promo.is_active ? 'Aktivan' : 'Neaktivan'}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => openEditPromo(promo)}
                            className="text-blue-600 hover:text-blue-500 font-medium text-sm"
                          >
                            Izmeni
                          </button>
                          <button
                            onClick={() => handleTogglePromo(promo.id)}
                            className={`font-medium text-sm ${promo.is_active ? 'text-yellow-600 hover:text-yellow-500' : 'text-green-600 hover:text-green-500'}`}
                          >
                            {promo.is_active ? 'Deaktiviraj' : 'Aktiviraj'}
                          </button>
                          <button
                            onClick={() => handleDeletePromo(promo.id)}
                            className="text-red-600 hover:text-red-500 font-medium text-sm"
                          >
                            Obriši
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create/Edit Promo Modal */}
      {(isCreatingPromo || isEditingPromo) && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4">
              {isEditingPromo ? 'Izmeni Promo Kod' : 'Novi Promo Kod'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Kod</label>
                <input
                  type="text"
                  value={newPromo.code}
                  onChange={(e) => setNewPromo({ ...newPromo, code: e.target.value.toUpperCase() })}
                  disabled={!!isEditingPromo}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white font-mono disabled:opacity-50"
                  placeholder="npr. PROMO2026"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Broj tokena</label>
                <input
                  type="number"
                  min="1"
                  value={newPromo.tokensAmount}
                  onChange={(e) => setNewPromo({ ...newPromo, tokensAmount: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Broj dostupnih vaučera</label>
                <input
                  type="number"
                  min="1"
                  value={newPromo.maxUses}
                  onChange={(e) => setNewPromo({ ...newPromo, maxUses: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                  placeholder="Ostavite prazno za neograničeno"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Važi do</label>
                <input
                  type="date"
                  value={newPromo.validUntil}
                  onChange={(e) => setNewPromo({ ...newPromo, validUntil: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Opis</label>
                <textarea
                  value={newPromo.description}
                  onChange={(e) => setNewPromo({ ...newPromo, description: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                  rows={3}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setIsCreatingPromo(false);
                  setIsEditingPromo(null);
                  setNewPromo({ code: '', tokensAmount: 5, maxUses: '', validUntil: '', description: '' });
                }}
                className="px-4 py-2 text-slate-600 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
              >
                Otkaži
              </button>
              <button
                onClick={isEditingPromo ? handleUpdatePromo : handleCreatePromo}
                className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600"
              >
                {isEditingPromo ? 'Sačuvaj' : 'Kreiraj'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
